import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { ClipboardCheck, ClipboardX, Eye, X, Loader2, AlertTriangle, Inbox, Save, Plane, MapPin, Package, Weight, UserCheck, UserCog, UserPlus, Lock, Pencil, FileText, CheckSquare, Calendar, RefreshCw } from 'lucide-react';
import { DateRangePicker, createStaticRanges } from 'react-date-range';
import {
  startOfDay, endOfDay, subDays,
  startOfWeek, endOfWeek, subWeeks,
  startOfMonth, endOfMonth, subMonths,
} from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { API_ENDPOINTS, apiCall } from '../../config/api';
import { getCloudFrontUrl } from '../../utils/s3Upload';
import { useAuth } from '../../context/AuthContext';
import ManualLeoUpload from './ManualLeoUpload';
import { PageHead, Panel, Pill, Modal, FormField, btn } from '../common/AdminUI';
import QuickCreateUserModal from './QuickCreateUserModal';
import RequestCustomerModal from './RequestCustomerModal';
import CustomerRequestsPanel from './CustomerRequestsPanel';

const PAGE_SIZE = 20;

// Quick ranges for the "Received" date filter — same set, same Monday-start
// weeks as the Accountant dashboard's shipment filter.
const WEEK = { weekStartsOn: 1 };
const DATE_PRESETS = createStaticRanges([
  { label: 'Today', range: () => ({ startDate: startOfDay(new Date()), endDate: endOfDay(new Date()) }) },
  { label: 'Yesterday', range: () => ({ startDate: startOfDay(subDays(new Date(), 1)), endDate: endOfDay(subDays(new Date(), 1)) }) },
  { label: 'This Week', range: () => ({ startDate: startOfWeek(new Date(), WEEK), endDate: endOfDay(new Date()) }) },
  { label: 'Last Week', range: () => ({ startDate: startOfWeek(subWeeks(new Date(), 1), WEEK), endDate: endOfWeek(subWeeks(new Date(), 1), WEEK) }) },
  { label: 'This Month', range: () => ({ startDate: startOfMonth(new Date()), endDate: endOfDay(new Date()) }) },
  { label: 'Last Month', range: () => ({ startDate: startOfMonth(subMonths(new Date(), 1)), endDate: endOfMonth(subMonths(new Date(), 1)) }) },
  { label: 'Last 7 Days', range: () => ({ startDate: startOfDay(subDays(new Date(), 6)), endDate: endOfDay(new Date()) }) },
  { label: 'Last 30 Days', range: () => ({ startDate: startOfDay(subDays(new Date(), 29)), endDate: endOfDay(new Date()) }) },
]);

const sameDay = (a, b) => a && b && startOfDay(a).getTime() === startOfDay(b).getTime();
const shortDate = (d) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

// Show the preset's own name when the picked range is exactly one of them —
// "This Week" reads better than "22 Sep 2026 - 24 Sep 2026".
const describeRange = (start, end) => {
  const preset = DATE_PRESETS.find((p) => {
    const r = p.range();
    return sameDay(r.startDate, start) && sameDay(r.endDate, end);
  });
  if (preset) return preset.label;
  return sameDay(start, end) ? shortDate(start) : `${shortDate(start)} - ${shortDate(end)}`;
};

// LEO PDF field comes back as an array, a JSON-stringified array, or a single string
// key, depending on how the record was created — normalize to a plain array of keys.
const normalizePdfList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch {
      return [value];
    }
    return [value];
  }
  return [value];
};

const openLeoPdfs = (shipment) => {
  const keys = normalizePdfList(shipment.leoPdf);
  if (keys.length === 0) { toast.error('LEO PDF not available for this shipment.'); return; }
  keys.forEach((key) => {
    const url = key.startsWith('http') ? key : getCloudFrontUrl(key);
    // window.open() with 'noopener' returns null even when the tab opened
    // fine, so its return value can't be read as "popup blocked" — clicking a
    // real link opens the PDF without that false warning.
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  });
};

// These fields come back from the automation scrape as JSON arrays (e.g. ["1234"]);
// flatten for display the same way the rest of the app does everywhere else.
const arrToDisplay = (v) => {
  if (Array.isArray(v)) return v.filter(Boolean).join(' & ');
  if (v === null || v === undefined) return '';
  return String(v);
};

// LEO PDFs often print "SAME AS CONSIGNEE" instead of repeating the name — resolve
// that to the actual consignee/customer name wherever buyer name is shown or edited.
const resolveBuyerName = (shipment) => {
  const buyer = arrToDisplay(shipment.buyerName).trim();
  if (/^same as consignee$/i.test(buyer)) {
    return arrToDisplay(shipment.customerName) || buyer;
  }
  return buyer;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Flight/S-B dates come back with a time component (e.g. "2026-07-11T00:56:00.000Z") —
// strip it down to just the date for display.
const dateOnly = (v) => {
  const s = String(v || '').trim();
  if (!s) return '';
  const d = new Date(s);
  if (!Number.isNaN(d.getTime()) && /\d{4}-\d{2}-\d{2}/.test(s)) {
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  // Already-formatted strings (e.g. "11/07/2026") — just drop anything after a space/T.
  return s.split(/[T ]/)[0];
};

const arrDatesToDisplay = (v) => {
  if (Array.isArray(v)) return v.filter(Boolean).map(dateOnly).join(' & ');
  return dateOnly(v);
};

// Mirrors the "Shipment Data" grid on the Employee Dashboard's own Edit Shipment
// form field-for-field, so a reviewer sees exactly the same picture of a shipment
// whether it arrived via automation or was entered by hand. Fields that carry an
// `editKey` are the ones PUT /awb/update actually accepts — those render as live
// inputs right in this same grid instead of a separate duplicate edit section.
const SHIPMENT_DATA_FIELDS = [
  { label: 'AWB No.', key: 'awbNumber' },
  { label: 'Origin Port Code', key: 'portCode' },
  { label: 'City Code', key: 'destCode' },
  { label: 'Dest. Country', key: 'destCountry' },
  { label: 'Dest. City', key: 'destCity' },
  { label: 'Total Pkt', key: 'numberOfPackages' },
  { label: 'CH Weight (kg)', key: 'chargeableWeight' },
  { label: 'S/B Date', key: 'sbDates', dateOnly: true },
  { label: 'S/B No.', key: 'sbNumbers' },
  { label: 'Consignee Name', key: 'customerName', editKey: 'customerName' },
  { label: 'Buyer Name', key: 'buyerName', editKey: 'buyer_name' },
  { label: 'FOB ₹', key: 'sbFobValues' },
  { label: 'IGST ₹', key: 'IGST', editKey: 'IGST' },
  { label: 'DBK ₹', key: 'DBK', editKey: 'DBK' },
  { label: 'Rodtep ₹', key: 'Rodtep', editKey: 'Rodtep' },
  { label: 'Rosctl ₹', key: 'Rosctl', editKey: 'Rosctl' },
  { label: 'Weight (kg)', key: 'grossWeight' },
  { label: 'FC Value', key: 'fcValue', editKey: 'fc_value' },
  { label: 'FC Type', key: 'fcType', editKey: 'fc_type' },
  { label: 'Flight Date', key: 'flightDates', dateOnly: true },
  { label: 'Flight No.', key: 'flightNumbers' },
  { label: 'Activity', key: 'paymentActivity' },
  { label: 'Amount ₹', key: 'paymentsAmount' },
  { label: 'FC Rate', key: 'fcRate', editKey: 'fc_rate' },
];

const fieldInputClass = 'w-full px-3.5 py-2.5 rounded-xl text-[13px] font-semibold outline-none transition ' +
  'bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 ' +
  'focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 ' +
  'dark:bg-slate-800/70 dark:border-slate-700 dark:text-slate-100';

// Called by each of the 3 dashboards (SuperAdmin / Admin / Employee) as the content
// of a "Pending Approval" tab. Self-contained: fetches its own data and manages its
// own modals; `onApproved` lets the parent refresh its main shipment list so an
// approved shipment shows up there immediately instead of only after a reload.
// `onCountChange` keeps the parent's tab-badge count in sync once this is mounted
// (the parent also does its own lightweight count fetch on load, for the badge to
// show even before this component has ever been opened).
// The queue is the same screen in every console, so the eyebrow is the one
// thing that has to know whose console it is sitting in.
const PendingApprovalQueue = ({ onApproved, onCountChange, eyebrow = 'Automation' }) => {
  const { user } = useAuth();
  // Manual LEO upload lives on the Super Admin dashboard only.
  const canManualUpload = user?.role === 'super_admin';
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  // fetchPending is a stable callback, so it can't read `shipments` directly —
  // a ref keeps the loaded count honest when appending the next page.
  const shipmentsRef = useRef([]);
  useEffect(() => { shipmentsRef.current = shipments; }, [shipments]);

  useEffect(() => { onCountChange?.(totalRecords); }, [totalRecords, onCountChange]);

  // A manual upload is processed asynchronously by the ingestion worker, so
  // give it 3s before re-fetching the queue.
  const refreshTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(refreshTimerRef.current), []);

  const [viewingShipment, setViewingShipment] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const [rejectingShipment, setRejectingShipment] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const [approvingAwb, setApprovingAwb] = useState(null);

  // Bulk approve: tick the cards you've already checked and send them in one
  // call. Only shipments that already have a user are selectable — the backend
  // rejects the rest per-item, so there's nothing to gain by picking them.
  const [selectedAwbs, setSelectedAwbs] = useState([]);
  const [queueFilter, setQueueFilter] = useState('all');   // 'all' | 'ready' | 'blocked'
  const [bulkApproving, setBulkApproving] = useState(false);

  // Filter on when the shipment was received (createdAt).
  const [dateRange, setDateRange] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const toggleSelect = (awbNumber) => {
    setSelectedAwbs(prev => (prev.includes(awbNumber) ? prev.filter(a => a !== awbNumber) : [...prev, awbNumber]));
  };

  // Customer list, for the "Select User" reassignment dropdown — automation sets
  // userID automatically only on an exact exporter-name match, so a human often
  // needs to confirm or pick the right customer manually before approving.
  const [customers, setCustomers] = useState([]);

  const loadCustomers = useCallback(async () => {
    try {
      const res = await apiCall(API_ENDPOINTS.GET_CUSTOMERS, { method: 'GET' });
      const result = await res.json();
      if (res.ok && Array.isArray(result.data)) {
        setCustomers(result.data.map(c => ({
          id: c.id,
          name: c.our_users || c.custName || c.name || c.username || 'Unknown',
        })));
      }
    } catch {
      // customer list is a convenience for the dropdown — fail silently
    }
  }, []);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  // Creating a customer for an unmatched exporter, without leaving the queue.
  const [creatingUser, setCreatingUser] = useState(null); // { shipment, reason, thenApprove }
  const [requestingCustomer, setRequestingCustomer] = useState(null); // { shipment, reason }
  const [requestsRefresh, setRequestsRefresh] = useState(0);
  // Accounts are created by admins today: an employee asks one to, and a super
  // admin neither creates nor asks — so an unassigned shipment simply isn't
  // theirs to act on until an admin has created the user.
  const canCreateUser = user?.role === 'admin';
  const canRequestUser = user?.role === 'employee';

  const customerNameById = useCallback((id) => {
    if (!id) return null;
    return customers.find(c => String(c.id) === String(id))?.name || null;
  }, [customers]);

  const fetchPending = useCallback(async (offset = 0, append = false) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const res = await apiCall(
        `${API_ENDPOINTS.GET_ALL_SHIPMENTS}?approvalStatus=PENDING_APPROVAL&limit=${PAGE_SIZE}&offset=${offset}&sortBy=createdAt&sortOrder=DESC`,
        { method: 'GET' }
      );
      const result = await res.json();
      if (res.ok && result.success) {
        const fetched = result.data?.shipments || [];
        const total = result.data?.totalRecords || 0;
        setShipments(prev => (append ? [...prev, ...fetched] : fetched));
        setTotalRecords(total);
        const loaded = (append ? shipmentsRef.current.length : 0) + fetched.length;
        setHasMore(fetched.length > 0 && loaded < total);
      } else {
        toast.error(result.message || 'Failed to load pending shipments');
      }
    } catch (err) {
      toast.error('Failed to load pending shipments');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchPending(0, false); }, [fetchPending]);

  const filterStart = dateRange?.[0]?.startDate ? startOfDay(dateRange[0].startDate) : null;
  const filterEnd = dateRange?.[0]?.endDate ? endOfDay(dateRange[0].endDate) : null;
  const dateFilterOn = Boolean(filterStart && filterEnd);
  const visibleShipments = dateFilterOn
    ? shipments.filter((sh) => {
        const received = new Date(sh.createdAt);
        return !Number.isNaN(received.getTime()) && received >= filterStart && received <= filterEnd;
      })
    : shipments;

  // The queue is paginated but the filter runs over what's already loaded, so
  // while a date filter is on keep pulling the remaining pages in — otherwise
  // it would silently hide matches still sitting on the server.
  useEffect(() => {
    if (dateFilterOn && hasMore && !loading && !loadingMore) fetchPending(shipmentsRef.current.length, true);
  }, [dateFilterOn, hasMore, loading, loadingMore, shipments.length, fetchPending]);

  const removeFromList = (awbNumber) => {
    setShipments(prev => prev.filter(s => s.awbNumber !== awbNumber));
    setTotalRecords(prev => Math.max(0, prev - 1));
    setSelectedAwbs(prev => prev.filter(a => a !== awbNumber));
    setViewingShipment(prev => (prev?.awbNumber === awbNumber ? null : prev));
  };

  // Which customer to have the "Select User" dropdown default to: the confirmed
  // match if there is one, else automation's best-guess suggestion, else blank
  // (left for a human to pick).
  const buildEditForm = (shipment) => {
    const form = { userID: shipment.userID || shipment.suggestedUserId || '' };
    SHIPMENT_DATA_FIELDS.forEach(({ key, editKey }) => {
      if (editKey) form[editKey] = editKey === 'buyer_name' ? resolveBuyerName(shipment) : arrToDisplay(shipment[key]);
    });
    return form;
  };

  const handleView = async (shipment, preselectUserId = null) => {
    const withPreselect = (form) => (preselectUserId && !form.userID ? { ...form, userID: String(preselectUserId) } : form);
    setViewingShipment(shipment);
    setEditForm(withPreselect(buildEditForm(shipment)));
    setViewLoading(true);
    try {
      const res = await apiCall(`${API_ENDPOINTS.GET_SHIPMENT}?service=${shipment.shipmentType}&awb=${shipment.awbNumber}`, { method: 'GET' });
      const result = await res.json();
      if (res.ok && result.success) {
        setViewingShipment(result.data);
        setEditForm(withPreselect(buildEditForm(result.data)));
      }
    } catch {
      // keep the already-loaded row data as a fallback
    } finally {
      setViewLoading(false);
    }
  };

  const closeView = () => { setViewingShipment(null); setEditForm({}); };

  const saveEdit = async (shipment, { keepOpen = false } = {}) => {
    setSavingEdit(true);
    try {
      const { userID, ...rest } = editForm;
      const payload = { ...rest };
      if (userID) payload.userID = Number(userID);
      const res = await apiCall(`${API_ENDPOINTS.UPDATE_AWB}?service=${shipment.shipmentType}&awb=${shipment.awbNumber}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (res.ok && result.success) {
        if (!keepOpen) toast.success('Shipment details updated');
        setShipments(prev => prev.map(s => (s.awbNumber === shipment.awbNumber ? { ...s, ...result.data } : s)));
        if (keepOpen) setViewingShipment(prev => (prev?.awbNumber === shipment.awbNumber ? { ...prev, ...result.data } : prev));
        else closeView();
        return true;
      }
      toast.error(result.message || 'Failed to update shipment');
      return false;
    } catch {
      toast.error('Failed to update shipment');
      return false;
    } finally {
      setSavingEdit(false);
    }
  };

  // From the view modal: persist any pending edits (esp. the assigned user)
  // first, then approve — so "pick a user → Approve" works in one go.
  const saveAndApprove = async (shipment) => {
    const dirty = JSON.stringify(editForm) !== JSON.stringify(buildEditForm(shipment));
    if (dirty) {
      const ok = await saveEdit(shipment, { keepOpen: true });
      if (!ok) return;
    }
    await approve({ ...shipment, userID: editForm.userID ? Number(editForm.userID) : shipment.userID });
  };

  const assignUser = async (shipment, userID) => {
    try {
      const res = await apiCall(`${API_ENDPOINTS.UPDATE_AWB}?service=${shipment.shipmentType}&awb=${shipment.awbNumber}`, {
        method: 'PUT',
        body: JSON.stringify({ userID: Number(userID) }),
      });
      const result = await res.json();
      if (res.ok && result.success) {
        setShipments(prev => prev.map(s => (s.awbNumber === shipment.awbNumber ? { ...s, ...result.data, userID: Number(userID) } : s)));
        return true;
      }
      toast.error(result.message || 'Could not assign the new user to this shipment');
      return false;
    } catch {
      toast.error('Could not assign the new user to this shipment');
      return false;
    }
  };

  // Exporter names come off a PDF, so match loosely (case/punctuation-insensitive).
  const findCustomerByName = (name) => {
    const norm = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const target = norm(name);
    if (target.length < 3) return null;
    return customers.find(c => norm(c.name) === target)
      || customers.find(c => norm(c.name).includes(target) || target.includes(norm(c.name)))
      || null;
  };

  // An unassigned card shows Create User instead of Approve — the account has
  // to exist and be on the shipment before approving means anything. If one
  // already matches the exporter, confirm that in the review modal rather
  // than creating a duplicate.
  const startUserForShipment = (shipment) => {
    const exporter = shipment.exporterName || shipment.customerName;
    const existing = findCustomerByName(exporter);
    if (existing) {
      toast(`"${existing.name}" already exists — confirm it here.`);
      handleView(shipment, existing.id);
      return;
    }
    setCreatingUser({
      shipment,
      reason: `No user matches "${exporter || 'this exporter'}". Create one and it'll be assigned to AWB ${shipment.awbNumber}.`,
      thenAssign: true,
      thenApprove: false,
    });
  };

  const approve = async (shipment) => {
    // Can't approve without a customer. If one already exists for this
    // exporter, open the review modal with it pre-selected; otherwise offer
    // to create it.
    if (!shipment.userID) {
      const exporter = shipment.exporterName || shipment.customerName;
      const existing = findCustomerByName(exporter);
      if (existing) {
        toast.error(`"${existing.name}" already exists — confirm and approve.`);
        handleView(shipment, existing.id);
        return;
      }
      const reason = `No user matches "${exporter || 'this exporter'}". ${canCreateUser ? `Create one to approve ${shipment.awbNumber}.` : `Ask your admin to create one so ${shipment.awbNumber} can be approved.`}`;
      if (canCreateUser) setCreatingUser({ shipment, reason, thenApprove: true });
      else if (canRequestUser) setRequestingCustomer({ shipment, reason });
      else toast.error(`No user matches "${exporter || 'this exporter'}" — an admin has to create it before AWB ${shipment.awbNumber} can be approved.`);
      return;
    }
    setApprovingAwb(shipment.awbNumber);
    try {
      const res = await apiCall(`/awb/${shipment.shipmentType}/${shipment.awbNumber}/approve`, { method: 'POST' });
      const result = await res.json();
      if (res.ok && result.success) {
        toast.success(`AWB ${shipment.awbNumber} approved`);
        removeFromList(shipment.awbNumber);
        if (viewingShipment?.awbNumber === shipment.awbNumber) closeView();
        onApproved?.(shipment);
      } else {
        toast.error(result.message || 'Failed to approve shipment');
      }
    } catch {
      toast.error('Failed to approve shipment');
    } finally {
      setApprovingAwb(null);
    }
  };

  // One call for the whole batch. The API processes every item independently,
  // so a single bad AWB doesn't stop the others — clear only the ones that
  // actually went through and keep the failures selected with their reason.
  const bulkApprove = async () => {
    const picked = visibleShipments.filter(s => selectedAwbs.includes(s.awbNumber) && s.userID);
    if (picked.length === 0) return;
    setBulkApproving(true);
    try {
      const res = await apiCall(API_ENDPOINTS.BULK_APPROVE_AWB, {
        method: 'POST',
        body: JSON.stringify({ items: picked.map(s => ({ service: s.shipmentType, awbNumber: s.awbNumber })) }),
      });
      const result = await res.json();
      if (!res.ok || !result.success) {
        toast.error(result.message || 'Failed to approve the selected shipments');
        return;
      }
      const results = Array.isArray(result.data?.results) ? result.data.results : [];
      const approved = results.filter(r => r.success).map(r => String(r.awbNumber));
      const failed = results.filter(r => !r.success);

      if (approved.length > 0) {
        setShipments(prev => prev.filter(s => !approved.includes(String(s.awbNumber))));
        setTotalRecords(prev => Math.max(0, prev - approved.length));
        setViewingShipment(prev => (prev && approved.includes(String(prev.awbNumber)) ? null : prev));
        toast.success(`${approved.length} shipment${approved.length > 1 ? 's' : ''} approved`);
        onApproved?.();
      }
      setSelectedAwbs(failed.map(r => String(r.awbNumber)));
      if (failed.length > 0) {
        toast.error(
          failed.length === 1
            ? `AWB ${failed[0].awbNumber}: ${failed[0].message || 'could not be approved'}`
            : `${failed.length} shipments could not be approved — first: AWB ${failed[0].awbNumber} (${failed[0].message || 'unknown error'})`,
          { duration: 6000 }
        );
      }
    } catch {
      toast.error('Failed to approve the selected shipments');
    } finally {
      setBulkApproving(false);
    }
  };

  const openReject = (shipment) => { setRejectingShipment(shipment); setRejectionReason(''); };
  const closeReject = () => { if (!rejecting) { setRejectingShipment(null); setRejectionReason(''); } };

  const confirmReject = async () => {
    if (!rejectingShipment) return;
    setRejecting(true);
    try {
      const res = await apiCall(`/awb/${rejectingShipment.shipmentType}/${rejectingShipment.awbNumber}/reject`, {
        method: 'POST',
        body: JSON.stringify({ rejectionReason: rejectionReason.trim() || undefined }),
      });
      const result = await res.json();
      if (res.ok && result.success) {
        toast.success(`AWB ${rejectingShipment.awbNumber} rejected`);
        removeFromList(rejectingShipment.awbNumber);
        setRejectingShipment(null);
        setRejectionReason('');
      } else {
        toast.error(result.message || 'Failed to reject shipment');
      }
    } catch {
      toast.error('Failed to reject shipment');
    } finally {
      setRejecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <Loader2 size={30} className="animate-spin text-blue-500 mb-3" />
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading pending shipments…</p>
      </div>
    );
  }

  const approvableAwbs = visibleShipments.filter(s => s.userID).map(s => s.awbNumber);
  const selectedCount = selectedAwbs.filter(a => approvableAwbs.includes(a)).length;
  const allSelected = approvableAwbs.length > 0 && selectedCount === approvableAwbs.length;
  const toggleSelectAll = () => setSelectedAwbs(allSelected ? [] : approvableAwbs);
  const checkboxClass = 'w-[18px] h-[18px] rounded accent-emerald-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40';

  // A card is ready when a customer is attached; without one it cannot be
  // approved, so those are what a reviewer has to act on first.
  const needsCustomer = visibleShipments.filter((s) => !s.userID);
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  const receivedToday = visibleShipments.filter((s) => {
    const d = new Date(s.createdAt);
    return !Number.isNaN(d.getTime()) && d >= startOfToday;
  }).length;

  const queueCards = [
    {
      id: 'all', label: 'In queue', value: dateFilterOn ? visibleShipments.length : totalRecords,
      note: dateFilterOn ? `of ${totalRecords} in all` : 'Awaiting review',
      bar: 'bg-amber-500', tone: 'text-amber-600 dark:text-amber-300',
    },
    {
      id: 'ready', label: 'Ready to approve', value: approvableAwbs.length,
      note: approvableAwbs.length ? 'Customer attached' : 'None ready',
      bar: 'bg-emerald-500', tone: 'text-emerald-600 dark:text-emerald-300',
    },
    {
      id: 'blocked', label: 'Needs a customer', value: needsCustomer.length,
      note: needsCustomer.length ? 'Assign before approving' : 'All assigned',
      bar: 'bg-rose-500', tone: 'text-rose-600 dark:text-rose-300',
    },
    {
      id: 'today', label: 'Received today', value: receivedToday,
      note: 'Since midnight', bar: 'bg-blue-500', tone: 'text-blue-600 dark:text-blue-300',
    },
  ];

  const queueRows = queueFilter === 'ready' ? visibleShipments.filter((s) => s.userID)
    : queueFilter === 'blocked' ? needsCustomer
    : visibleShipments;

  return (
    <div>
      <PageHead
        eyebrow={eyebrow}
        title="Pending Approval"
        badge={<Pill tone="amber" dot>{dateFilterOn ? `${visibleShipments.length} of ${totalRecords}` : `${totalRecords} in queue`}</Pill>}
        subtitle="Shipments captured automatically from incoming email — review each one before it joins the main shipment list."
        actions={(
          <>
            <button type="button" onClick={() => setShowDatePicker(true)}
              className={`${btn.quiet} ${dateFilterOn ? 'border-blue-300 text-blue-700 dark:border-blue-500/50 dark:text-blue-300' : ''}`}>
              <Calendar size={16} />
              {dateFilterOn ? describeRange(filterStart, filterEnd) : 'All dates'}
            </button>
            {dateFilterOn && (
              <button type="button" onClick={() => setDateRange(null)} title="Clear date filter"
                className={`${btn.quiet} w-10 px-0 justify-center`}>
                <X size={16} />
              </button>
            )}
            <button type="button" onClick={() => fetchPending(0)} title="Reload the queue"
              className={`${btn.quiet} w-10 px-0 justify-center`}>
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </>
        )}
      />

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
        {queueCards.map((c) => {
          const clickable = c.id !== 'today';
          const on = queueFilter === c.id;
          return (
            <button key={c.id} type="button" disabled={!clickable}
              onClick={clickable ? () => setQueueFilter(on ? 'all' : c.id) : undefined}
              className={`relative overflow-hidden rounded-2xl border px-4 pt-4 pb-5 text-left transition
                          ${!clickable ? 'cursor-default ' : ''}${on
                            ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-500/20 dark:border-blue-500/60 dark:bg-blue-500/10'
                            : 'border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-[#0e1626] dark:hover:bg-slate-800/40'}`}>
              <p className="text-[10.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">{c.label}</p>
              <p className="text-[26px] leading-none font-black text-slate-900 dark:text-white mt-2">{c.value}</p>
              <p className={`text-[11.5px] font-bold mt-1.5 ${c.tone}`}>{c.note}</p>
              <span className={`absolute left-0 right-0 bottom-0 h-[3px] ${c.bar}`} />
            </button>
          );
        })}
      </div>

      {canManualUpload && (
        <ManualLeoUpload
          onQueued={() => {
            clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = setTimeout(() => fetchPending(0), 3000);
          }}
        />
      )}

      {queueRows.length === 0 ? (
        <Panel className="flex flex-col items-center justify-center py-20 text-center">
          <span className={`grid place-items-center w-14 h-14 rounded-2xl mb-4 ${
            dateFilterOn
              ? 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300'
              : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300'
          }`}>
            {dateFilterOn ? <Calendar size={26} /> : <ClipboardCheck size={28} />}
          </span>
          <p className="text-[14px] font-bold text-slate-800 dark:text-slate-100">
            {dateFilterOn ? 'Nothing received in this range' : 'All caught up'}
          </p>
          <p className="text-[12.5px] text-slate-500 dark:text-slate-400 mt-1">
            {dateFilterOn
              ? `No pending shipments were received in ${describeRange(filterStart, filterEnd)}.`
              : 'No shipments are waiting for approval right now.'}
          </p>
          {dateFilterOn && (
            <button type="button" onClick={() => setDateRange(null)} className={`${btn.quiet} mt-4`}>
              Clear date filter
            </button>
          )}
        </Panel>
      ) : (
        <div className={`space-y-3.5 ${selectedCount > 0 ? 'pb-20' : ''}`}>
          {approvableAwbs.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl px-4 py-3.5
                            border border-slate-200 bg-white shadow-sm
                            dark:border-slate-800 dark:bg-[#0e1626] dark:shadow-none">
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  disabled={bulkApproving}
                  className={checkboxClass}
                />
                <span className="text-[13.5px] font-bold text-slate-800 dark:text-slate-100">
                  {selectedCount > 0
                    ? `${selectedCount} of ${approvableAwbs.length} selected`
                    : `Select all ${approvableAwbs.length} ready to approve`}
                </span>
                {selectedCount > 0 && <Pill tone="emerald">{selectedCount} picked</Pill>}
              </label>
              <p className="text-[11.5px] text-slate-500 dark:text-slate-400">
                Tick the shipments you've reviewed, then approve them together.
              </p>
            </div>
          )}
          {(() => {
          const renderCard = (shipment) => {
            const isApproving = approvingAwb === shipment.awbNumber;
            const missing = Array.isArray(shipment.missingFields) ? shipment.missingFields : [];
            const unassigned = !shipment.userID;
            const selected = selectedAwbs.includes(shipment.awbNumber);
            const selectable = !unassigned && !bulkApproving;
            // Anywhere on the card toggles the tick — except the controls, which
            // keep their own behaviour, and any text the reviewer is selecting.
            const toggleFromCard = (e) => {
              if (e.target.closest('button, input, select, textarea, a, label')) return;
              if (window.getSelection?.()?.toString()) return;
              toggleSelect(shipment.awbNumber);
            };
            const assignedName = shipment.userID
              ? (customerNameById(shipment.userID) || shipment.username || `User #${shipment.userID}`)
              : null;
            const suggestedName = !shipment.userID && shipment.suggestedUserId
              ? (customerNameById(shipment.suggestedUserId) || `User #${shipment.suggestedUserId}`)
              : null;
            const facts = [
              { label: 'Origin port', value: shipment.portCode },
              { label: 'Destination', value: [shipment.destCity, shipment.destCountry].filter(Boolean).join(', ') || shipment.destCode },
              { label: 'Packages', value: shipment.numberOfPackages },
              { label: 'Gross weight', value: shipment.grossWeight ? `${shipment.grossWeight} kg` : null },
              { label: 'Shipping bill', value: arrToDisplay(shipment.sbNumbers), sub: arrToDisplay(shipment.sbDates) },
              { label: 'Assigned user', value: assignedName || (suggestedName ? `${suggestedName} (suggested)` : null), warn: !shipment.userID },
            ];
            const incentives = [
              ['DBK', shipment.DBK ?? shipment.dbk],
              ['RoDTEP', shipment.Rodtep ?? shipment.rodtep],
              ['RoSCTL', shipment.Rosctl ?? shipment.rosctl],
              ['IGST', shipment.IGST ?? shipment.igst],
            ].filter(([, v]) => v !== undefined && v !== null && v !== '' && String(v) !== '0');

            return (
              <div
                key={shipment.awbNumber}
                onClick={selectable ? toggleFromCard : undefined}
                onKeyDown={selectable ? (e) => {
                  if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    toggleSelect(shipment.awbNumber);
                  }
                } : undefined}
                role={selectable ? 'button' : undefined}
                tabIndex={selectable ? 0 : undefined}
                aria-pressed={selectable ? selected : undefined}
                className={`rounded-2xl border border-l-4 overflow-hidden outline-none transition
                            bg-white dark:bg-[#0e1626]
                            ${unassigned
                              ? 'border-slate-200 border-l-rose-500 dark:border-slate-800 dark:border-l-rose-500'
                              : 'border-slate-200 border-l-amber-400 dark:border-slate-800 dark:border-l-amber-500'}
                            ${selectable ? 'cursor-pointer hover:border-slate-300 dark:hover:border-slate-700' : ''}
                            ${selected ? 'ring-2 ring-emerald-500/60 dark:ring-emerald-400/60' : ''}`}
              >
                {/* who and what, with the controls */}
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 px-4 sm:px-5 py-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleSelect(shipment.awbNumber)}
                      disabled={unassigned || bulkApproving}
                      title={unassigned ? 'Assign a user before this can be approved' : 'Select for bulk approval'}
                      className={`${checkboxClass} mt-1 flex-shrink-0`}
                    />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="flex items-center gap-1.5 font-mono font-black text-[15.5px] text-slate-900 dark:text-white">
                          <Plane size={14} className="text-slate-400" />{shipment.awbNumber}
                        </span>
                        {shipment.shipmentType && <Pill tone="blue">{shipment.shipmentType}</Pill>}
                        {normalizePdfList(shipment.leoPdf).length > 0 && <Pill tone="violet">LEO attached</Pill>}
                        {shipment.lowConfidence && <Pill tone="amber">Low confidence</Pill>}
                        {missing.length > 0 && <Pill tone="rose">{missing.length} field{missing.length > 1 ? 's' : ''} missing</Pill>}
                        {unassigned && <Pill tone="rose" dot>Needs a customer</Pill>}
                      </div>

                      <p className="text-[13.5px] font-bold text-slate-700 dark:text-slate-200 mt-2">
                        {shipment.exporterName || shipment.customerName || shipment.username || 'Unmatched exporter'}
                        {shipment.destCity && (
                          <span className="font-medium text-slate-400 dark:text-slate-500">
                            {' '}→ {shipment.destCity}{shipment.destCountry ? `, ${shipment.destCountry}` : ''}
                          </span>
                        )}
                      </p>
                      <p className="text-[11.5px] text-slate-400 dark:text-slate-500 mt-0.5">
                        Received {formatDate(shipment.createdAt)}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 lg:justify-end flex-shrink-0">
                    {normalizePdfList(shipment.leoPdf).length > 0 && (
                      <button onClick={() => openLeoPdfs(shipment)} title="View LEO PDF"
                        className="inline-flex items-center gap-2 h-9 px-3 rounded-xl text-[12.5px] font-bold border transition
                                   border-violet-200 text-violet-700 hover:bg-violet-50
                                   dark:border-violet-500/30 dark:text-violet-300 dark:hover:bg-violet-500/10">
                        <FileText size={14} /> LEO doc
                      </button>
                    )}
                    <button onClick={() => handleView(shipment)} title="View and edit every field"
                      className="inline-flex items-center gap-2 h-9 px-3 rounded-xl text-[12.5px] font-bold border transition
                                 border-slate-200 text-slate-600 hover:bg-slate-50
                                 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800">
                      <Eye size={14} /> Inspect
                    </button>
                    <button onClick={() => openReject(shipment)} disabled={isApproving}
                      className="inline-flex items-center gap-2 h-9 px-3 rounded-xl text-[12.5px] font-bold border transition disabled:opacity-50
                                 border-rose-200 text-rose-600 hover:bg-rose-50
                                 dark:border-rose-500/30 dark:text-rose-400 dark:hover:bg-rose-500/10">
                      <ClipboardX size={14} /> Reject
                    </button>
                    {unassigned && canRequestUser ? (
                      <button
                        onClick={() => setRequestingCustomer({ shipment, reason: `No user matches "${shipment.exporterName || shipment.customerName || 'this exporter'}". Ask your admin to create one so ${shipment.awbNumber} can be approved.` })}
                        title="Ask your admin to create this user and assign it"
                        className="inline-flex items-center gap-2 h-9 px-3.5 rounded-xl text-[12.5px] font-bold text-white bg-blue-600 hover:bg-blue-700 transition">
                        <UserPlus size={14} /> Request user
                      </button>
                    ) : unassigned ? (
                      <button onClick={() => startUserForShipment(shipment)}
                        title="Create the user for this shipment — Approve appears once it's assigned"
                        className="inline-flex items-center gap-2 h-9 px-3.5 rounded-xl text-[12.5px] font-bold text-white bg-blue-600 hover:bg-blue-700 transition">
                        <UserPlus size={14} /> Create user
                      </button>
                    ) : (
                      <button onClick={() => approve(shipment)} disabled={isApproving}
                        className="inline-flex items-center gap-2 h-9 px-3.5 rounded-xl text-[12.5px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition disabled:opacity-50">
                        {isApproving ? <Loader2 size={14} className="animate-spin" /> : <ClipboardCheck size={14} />}
                        Approve &amp; release
                      </button>
                    )}
                  </div>
                </div>

                {/* the figures a reviewer checks before approving */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border-t border-slate-100 dark:border-slate-800">
                  {facts.map((f, i) => (
                    <div key={f.label}
                      className={`px-4 py-3 min-w-0 border-slate-100 dark:border-slate-800
                                  ${i % 2 === 0 ? 'border-r sm:border-r' : ''} ${i < facts.length - 1 ? 'lg:border-r' : ''}
                                  ${i < (facts.length - 2) ? 'border-b sm:border-b lg:border-b-0' : ''}`}>
                      <p className="text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">{f.label}</p>
                      <p className={`text-[13px] font-bold truncate mt-1 ${
                        f.warn && f.value ? 'text-amber-600 dark:text-amber-400'
                          : f.value ? 'text-slate-800 dark:text-slate-100'
                          : 'text-slate-300 dark:text-slate-700'}`}>
                        {f.value || '—'}
                      </p>
                      {f.sub && <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">{f.sub}</p>}
                    </div>
                  ))}
                </div>

                {/* the commercial side, as the LEO reports it */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 px-4 sm:px-5 py-4 border-t border-slate-100 dark:border-slate-800
                                bg-slate-50/70 dark:bg-slate-900/40">
                  <div className="min-w-0">
                    <p className="text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Consignee &amp; buyer</p>
                    <p className="text-[13px] font-bold text-slate-800 dark:text-slate-100 truncate mt-1">
                      {arrToDisplay(shipment.consigneeName) || arrToDisplay(shipment.customerName) || '—'}
                    </p>
                    {arrToDisplay(shipment.buyerName) && (
                      <p className="text-[11.5px] text-slate-500 dark:text-slate-400 truncate">Buyer: {arrToDisplay(shipment.buyerName)}</p>
                    )}
                  </div>

                  <div className="min-w-0">
                    <p className="text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Flight schedule</p>
                    <p className="text-[13px] font-bold text-slate-800 dark:text-slate-100 truncate mt-1">
                      {arrToDisplay(shipment.flightNumbers) || '—'}
                    </p>
                    {arrToDisplay(shipment.flightDates) && (
                      <p className="text-[11.5px] text-slate-500 dark:text-slate-400 truncate">{arrToDisplay(shipment.flightDates)}</p>
                    )}
                  </div>

                  <div className="min-w-0">
                    <p className="text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">FOB &amp; incentives</p>
                    <p className="text-[13px] font-bold text-slate-800 dark:text-slate-100 truncate mt-1">
                      {arrToDisplay(shipment.sbFobValues) ? `₹${arrToDisplay(shipment.sbFobValues)}` : '—'}
                    </p>
                    {incentives.length > 0 && (
                      <p className="text-[11.5px] text-slate-500 dark:text-slate-400 truncate">
                        {incentives.map(([k, v]) => `${k} ₹${v}`).join(' · ')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          };

          const unassigned = queueRows.filter((sh) => !sh.userID);
          const assigned = queueRows.filter((sh) => !!sh.userID);
          return (
            <>
              {unassigned.length > 0 && (
                <div className="rounded-2xl border-2 border-red-200 dark:border-red-900/60 bg-red-50/60 dark:bg-red-950/20 p-3 sm:p-4 space-y-3">
                  <div className="flex items-center gap-2.5 px-1">
                    <span className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/40 flex items-center justify-center flex-shrink-0">
                      <UserCog size={16} className="text-red-600 dark:text-red-400" />
                    </span>
                    <div>
                      <h4 className="font-bold text-red-800 dark:text-red-300 leading-tight">
                        Needs User Assignment <span className="font-semibold text-red-500 dark:text-red-400">({unassigned.length})</span>
                      </h4>
                      <p className="text-xs text-red-600/80 dark:text-red-400/80">The automation couldn't match these to a user — assign one before approving.</p>
                    </div>
                  </div>
                  {unassigned.map(renderCard)}
                </div>
              )}
              {assigned.map(renderCard)}
            </>
          );
          })()}

          {hasMore && (
            <div className="flex justify-center pt-2">
              <button
                onClick={() => fetchPending(shipments.length, true)}
                disabled={loadingMore}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-slate-600 dark:text-slate-300 border-2 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition disabled:opacity-50"
              >
                {loadingMore && <Loader2 size={14} className="animate-spin" />}
                Load more
              </button>
            </div>
          )}
        </div>
      )}

      {/* Received-date picker — quick presets down the side, custom range on the
          calendar; two months on desktop, one stacked on a phone. */}
      {showDatePicker && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowDatePicker(false)}>
          <div
            className="rounded-2xl shadow-2xl overflow-hidden w-full max-w-fit max-h-[92vh] flex flex-col
                       bg-white border border-slate-200 dark:bg-[#0b1220] dark:border-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-6 px-5 py-3.5 flex-shrink-0
                            border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2.5">
                <span className="grid place-items-center w-9 h-9 rounded-xl
                                 bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
                  <Calendar size={16} />
                </span>
                <div>
                  <h3 className="text-[14px] font-bold text-slate-900 dark:text-white leading-tight">Filter by received date</h3>
                  <p className="text-[11.5px] text-slate-500 dark:text-slate-400">A quick range down the side, or pick your own days.</p>
                </div>
              </div>
              <button onClick={() => setShowDatePicker(false)}
                className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600
                           dark:hover:bg-slate-800 dark:hover:text-slate-200 transition">
                <X size={18} />
              </button>
            </div>

            <div className="ql-daterange overflow-auto flex-1">
              <div className="hidden sm:block">
                <DateRangePicker
                  ranges={dateRange || [{ startDate: new Date(), endDate: new Date(), key: 'selection' }]}
                  onChange={(item) => setDateRange([item.selection])}
                  staticRanges={DATE_PRESETS}
                  inputRanges={[]}
                  months={2}
                  direction="horizontal"
                  showSelectionPreview
                  moveRangeOnFirstSelection={false}
                  minDate={new Date(2020, 0, 1)}
                  maxDate={new Date()}
                  rangeColors={['#2563eb']}
                />
              </div>
              <div className="block sm:hidden">
                <DateRangePicker
                  ranges={dateRange || [{ startDate: new Date(), endDate: new Date(), key: 'selection' }]}
                  onChange={(item) => setDateRange([item.selection])}
                  staticRanges={DATE_PRESETS}
                  inputRanges={[]}
                  months={1}
                  direction="vertical"
                  showSelectionPreview
                  moveRangeOnFirstSelection={false}
                  minDate={new Date(2020, 0, 1)}
                  maxDate={new Date()}
                  rangeColors={['#2563eb']}
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 px-4 py-3 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
              <button
                onClick={() => { setDateRange(null); setShowDatePicker(false); }}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 dark:text-slate-300 border-2 border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition"
              >
                All dates
              </button>
              <button
                onClick={() => setShowDatePicker(false)}
                className="px-5 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md transition"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating bulk bar — stays reachable however far down the queue you've
          scrolled; sits below the modals' z-50 so it never covers one. */}
      {selectedCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-4 pointer-events-none">
          <div className="pointer-events-auto flex flex-wrap items-center gap-3 rounded-2xl px-4 py-3 shadow-2xl
                          border border-slate-200 bg-white dark:border-slate-700 dark:bg-[#111b2e]">
            <span className="flex items-center gap-2 text-[13px] font-bold text-slate-800 dark:text-slate-100">
              <CheckSquare size={16} className="text-emerald-600 dark:text-emerald-400" />
              {selectedCount} selected
            </span>
            <button type="button" onClick={() => setSelectedAwbs([])} disabled={bulkApproving}
              className={`${btn.quiet} h-9 disabled:opacity-50`}>
              Clear
            </button>
            <button type="button" onClick={bulkApprove} disabled={bulkApproving}
              className={`${btn.green} h-9 disabled:opacity-50`}>
              {bulkApproving ? <Loader2 size={14} className="animate-spin" /> : <ClipboardCheck size={14} />}
              {bulkApproving ? 'Approving…' : `Approve ${selectedCount}`}
            </button>
          </div>
        </div>
      )}

      {requestingCustomer && (
        <RequestCustomerModal
          shipment={requestingCustomer.shipment}
          notice={requestingCustomer.reason}
          onClose={() => setRequestingCustomer(null)}
          onSent={() => {
            setRequestsRefresh(n => n + 1);
            // Same as the admin side after a create: re-read the queue so the
            // card reflects whatever the server now holds for this shipment.
            fetchPending(0, false);
          }}
        />
      )}

      {creatingUser && (
        <QuickCreateUserModal
          exporterName={creatingUser.shipment?.exporterName || creatingUser.shipment?.customerName}
          notice={creatingUser.reason}
          onClose={() => setCreatingUser(null)}
          onCreated={async (created) => {
            const ctx = creatingUser;
            await loadCustomers();
            if (created?.id && viewingShipment?.awbNumber === ctx.shipment?.awbNumber) {
              setEditForm(prev => ({ ...prev, userID: String(created.id) }));
            }
            if (created?.id && ctx.shipment && (ctx.thenAssign || ctx.thenApprove)) {
              const assigned = await assignUser(ctx.shipment, created.id);
              if (assigned && ctx.thenApprove) await approve({ ...ctx.shipment, userID: created.id });
              else if (assigned) {
                toast.success(`Assigned to AWB ${ctx.shipment.awbNumber}`);
                // Re-read the queue so the card shows the server's own view of
                // the shipment instead of the copy patched here.
                fetchPending(0, false);
              }
            }
          }}
        />
      )}

      {/* ── View & Edit modal — mirrors the Employee Dashboard's own Edit Shipment modal chrome ── */}
      {viewingShipment && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
          <div
            className="bg-white dark:bg-[#0b1220] rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-h-[94vh] flex flex-col overflow-hidden"
            style={{ maxWidth: 'min(96vw, 940px)' }}
          >
            <div className="flex items-start justify-between gap-3 px-5 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Plane size={19} className="text-white" />
                </span>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-white leading-tight truncate">Shipment Details</h2>
                  <p className="text-xs text-blue-100 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-mono font-semibold">{viewingShipment.awbNumber}</span>
                    <span className="px-1.5 py-0.5 rounded bg-white/20 font-semibold tracking-wide">{viewingShipment.shipmentType?.toUpperCase()}</span>
                    {!editForm.userID && (
                      <span className="px-1.5 py-0.5 rounded bg-red-500/90 font-semibold tracking-wide">NO USER</span>
                    )}
                  </p>
                </div>
              </div>
              <button onClick={closeView} className="text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-lg transition flex-shrink-0">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
              {viewLoading ? (
                <div className="flex justify-center py-14"><Loader2 size={26} className="animate-spin text-blue-500" /></div>
              ) : (
                <>
                  {/* Automation review context — separate from the shipment's own commercial data */}
                  <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0e1626] p-4">
                    <div className="flex items-center gap-2.5 mb-3">
                      <span className="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300 grid place-items-center">
                        <UserCog size={15} />
                      </span>
                      <div>
                        <h3 className="text-[13.5px] font-bold text-slate-900 dark:text-white">Assigned user / entity</h3>
                        <p className="text-[11.5px] text-slate-500 dark:text-slate-400">What the automation read, and who it bills to.</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                      <div className="rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 px-3.5 py-2.5">
                        <div className="text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500 mb-0.5">Exporter (from PDF)</div>
                        <p className="text-[13px] font-bold text-slate-900 dark:text-white break-words">{viewingShipment.exporterName || '—'}</p>
                      </div>
                      <div className="rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 px-3.5 py-2.5">
                        <div className="text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500 mb-0.5">Received</div>
                        <p className="text-[13px] font-bold text-slate-900 dark:text-white">{formatDate(viewingShipment.createdAt)}</p>
                      </div>
                    </div>

                    <label className="block text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500 mb-1.5">
                      Assigned user {!editForm.userID && <span className="text-rose-500">· required to approve</span>}
                    </label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <select
                        value={editForm.userID ?? ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, userID: e.target.value }))}
                        className={`ql-select ${fieldInputClass} flex-1 ${!editForm.userID ? 'border-red-400 dark:border-red-500' : ''}`}
                      >
                        <option value="">— Select a user —</option>
                        {customers.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      {canRequestUser && (
                        <button
                          type="button"
                          onClick={() => setRequestingCustomer({ shipment: viewingShipment, reason: null })}
                          className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition whitespace-nowrap"
                          title="Ask your admin to create this user and assign it"
                        >
                          <UserPlus size={15} /> Ask Admin
                        </button>
                      )}
                      {canCreateUser && (
                        <button
                          type="button"
                          onClick={() => setCreatingUser({ shipment: viewingShipment, reason: null, thenApprove: false })}
                          className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition whitespace-nowrap"
                          title="Create a new user and assign it to this shipment"
                        >
                          <UserPlus size={15} /> New User
                        </button>
                      )}
                    </div>
                    {!viewingShipment.userID && viewingShipment.suggestedUserId && (
                      <p className="text-xs text-amber-800 dark:text-amber-300 mt-2">
                        Automation suggested <span className="font-semibold">{customerNameById(viewingShipment.suggestedUserId) || `User #${viewingShipment.suggestedUserId}`}</span> — confirm or change it.
                      </p>
                    )}
                    {Array.isArray(viewingShipment.missingFields) && viewingShipment.missingFields.length > 0 && (
                      <div className="mt-3 rounded-lg bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 px-3 py-2">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400 mb-0.5">Missing fields</div>
                        <p className="text-sm font-medium text-red-700 dark:text-red-300">{viewingShipment.missingFields.join(', ')}</p>
                      </div>
                    )}
                  </section>

                  {/* LEO documents — its own section since there can be more than one PDF
                      (one per Shipping Bill), and cramming that into a grid cell doesn't scale */}
                  {(() => {
                    const leoFiles = normalizePdfList(viewingShipment.leoPdf);
                    return (
                      <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0e1626] p-4">
                        <div className="flex items-center gap-2.5 mb-3">
                          <span className="w-8 h-8 rounded-lg bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300 grid place-items-center">
                            <FileText size={15} />
                          </span>
                          <div>
                            <h3 className="text-[13.5px] font-bold text-slate-900 dark:text-white">
                              LEO document repository
                            </h3>
                            <p className="text-[11.5px] text-slate-500 dark:text-slate-400">
                              {leoFiles.length > 0
                                ? `${leoFiles.length} file${leoFiles.length > 1 ? 's' : ''} attached — one per shipping bill.`
                                : 'Nothing attached to this shipment.'}
                            </p>
                          </div>
                        </div>
                        {leoFiles.length > 0 ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {leoFiles.map((keyOrUrl, idx) => {
                              const url = keyOrUrl.startsWith('http') ? keyOrUrl : getCloudFrontUrl(keyOrUrl);
                              const sbNo = Array.isArray(viewingShipment.sbNumbers) ? viewingShipment.sbNumbers[idx] : null;
                              return (
                                <a
                                  key={idx}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="group flex items-center gap-3 px-3 py-2.5 bg-white dark:bg-slate-800 border border-violet-200 dark:border-violet-800/50 rounded-lg hover:border-violet-400 dark:hover:border-violet-600 hover:shadow-sm transition-all"
                                >
                                  <span className="w-9 h-9 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center flex-shrink-0">
                                    <FileText size={16} className="text-violet-600 dark:text-violet-400" />
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                                      LEO Document {leoFiles.length > 1 ? idx + 1 : ''}
                                    </p>
                                    <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">
                                      {sbNo ? `S/B ${sbNo}` : 'Click to view'}
                                    </p>
                                  </div>
                                  <Eye size={15} className="text-slate-300 dark:text-slate-600 group-hover:text-violet-500 dark:group-hover:text-violet-400 transition-colors flex-shrink-0" />
                                </a>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-violet-700/70 dark:text-violet-300/70">No LEO PDF uploaded yet.</p>
                        )}
                      </section>
                    );
                  })()}

                  {/* Shipment Data — same field set, same layout, as the Edit Shipment form.
                      Fields the backend actually allows editing render as live inputs right here. */}
                  <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0e1626] p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <div className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300 grid place-items-center">
                          <Package size={15} />
                        </span>
                        <div>
                          <h3 className="text-[13.5px] font-bold text-slate-900 dark:text-white">Shipment data matrix</h3>
                          <p className="text-[11.5px] text-slate-500 dark:text-slate-400">Everything the LEO reported, as it will be stored.</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-400 dark:text-slate-500">
                          <Lock size={11} /> Customs locked
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-blue-600 dark:text-blue-400">
                          <Pencil size={11} /> Operator editable
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {SHIPMENT_DATA_FIELDS.map(({ label, key, dateOnly: isDateOnly, editKey }) => {
                        if (editKey) {
                          return (
                            <div key={key}>
                              <label className="flex items-center justify-between gap-2 mb-1.5">
                                <span className="text-[9.5px] font-black uppercase tracking-[0.12em] text-blue-600 dark:text-blue-400">{label}</span>
                                <Pencil size={11} className="text-blue-500 flex-shrink-0" />
                              </label>
                              <input
                                type="text"
                                value={editForm[editKey] ?? ''}
                                onChange={(e) => setEditForm(prev => ({ ...prev, [editKey]: e.target.value }))}
                                className="w-full h-11 px-3.5 rounded-xl text-[13px] font-bold outline-none transition
                                           bg-white border border-blue-300 text-slate-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10
                                           dark:bg-slate-900/60 dark:border-blue-500/50 dark:text-white"
                              />
                            </div>
                          );
                        }
                        const display = isDateOnly ? arrDatesToDisplay(viewingShipment[key]) : arrToDisplay(viewingShipment[key]);
                        return (
                          <div key={key}>
                            <label className="flex items-center justify-between gap-2 mb-1.5">
                              <span className="text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">{label}</span>
                              <Lock size={11} className="text-slate-300 dark:text-slate-600 flex-shrink-0" />
                            </label>
                            <div className="min-h-[44px] px-3.5 py-2.5 rounded-xl flex items-center select-none
                                            bg-slate-50 border border-slate-200 text-[13px] font-bold text-slate-700
                                            dark:bg-slate-900/50 dark:border-slate-800 dark:text-slate-200">
                              {display || <span className="text-slate-300 dark:text-slate-700">—</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>

                </>
              )}
            </div>

            {!viewLoading && (
              <div className="flex flex-wrap justify-end gap-2.5 px-5 py-4 flex-shrink-0
                              border-t border-slate-100 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40">
                <button onClick={closeView} disabled={savingEdit || approvingAwb === viewingShipment.awbNumber}
                  className={`${btn.quiet} disabled:opacity-50`}>
                  Cancel
                </button>
                <button
                  onClick={() => saveEdit(viewingShipment)}
                  disabled={savingEdit || approvingAwb === viewingShipment.awbNumber}
                  className={`${btn.primary} disabled:opacity-50`}
                >
                  {savingEdit ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                  Save changes
                </button>
                <button
                  onClick={() => saveAndApprove(viewingShipment)}
                  disabled={savingEdit || approvingAwb === viewingShipment.awbNumber}
                  title={!editForm.userID ? 'Assign a user before approving' : undefined}
                  className={`${btn.green} disabled:opacity-50`}
                >
                  {approvingAwb === viewingShipment.awbNumber ? <Loader2 size={15} className="animate-spin" /> : <ClipboardCheck size={15} />}
                  Approve &amp; release
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Reject reason modal ── */}
      {rejectingShipment && (
        <Modal
          open
          onClose={closeReject}
          icon={ClipboardX}
          tone="rose"
          title="Reject shipment"
          subtitle={`${rejectingShipment.awbNumber} leaves this queue and is marked rejected.`}
          width="max-w-md"
          footer={(
            <div className="flex flex-col-reverse sm:flex-row gap-2.5">
              <button type="button" onClick={closeReject} disabled={rejecting}
                className={`${btn.quiet} disabled:opacity-50`}>
                Cancel
              </button>
              <button type="button" onClick={confirmReject} disabled={rejecting}
                className="inline-flex items-center justify-center gap-2 h-10 px-4 flex-1 rounded-xl text-sm font-bold
                           text-white bg-rose-600 hover:bg-rose-700 transition disabled:opacity-50">
                {rejecting && <Loader2 size={14} className="animate-spin" />}
                Reject shipment
              </button>
            </div>
          )}
        >
          <FormField label="Reason" hint="(optional)">
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
              placeholder="e.g. AWB number does not match any known customer"
              className={`${fieldInputClass} resize-none`}
            />
          </FormField>
          <p className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-3">
            The reason is stored with the shipment, so whoever uploaded it can see why it came back.
          </p>
        </Modal>
      )}
    </div>
  );
};

export default PendingApprovalQueue;
